import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Logo3DAnimated } from './Logo3DAnimated';
import { Menu, X, UserRound } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

const AccountLink = ({ user }) => (
  <a href={user ? '/mon-espace' : '/connexion'} data-testid="header-account-link" title={user ? 'Mon espace' : 'Connexion'}
    className="flex items-center gap-2 text-sm tracking-wide text-gray-300 hover:text-gold transition-colors duration-300 uppercase">
    {user?.picture ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full ring-1 ring-gold/60 object-cover" /> : <UserRound className="w-5 h-5" />}
    <span className="hidden xl:inline">{user ? 'Mon espace' : 'Connexion'}</span>
  </a>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user } = useAuth();

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = `/#${id}`;
    }
    setMobileMenuOpen(false); // Close mobile menu after navigation
  };

  const handleServicesClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled 
          ? 'bg-[#121212]/90 backdrop-blur-xl shadow-2xl border-b border-gold/15' 
          : 'bg-gradient-to-b from-black/70 via-black/35 to-transparent'
      }`}
    >
      <div className="container mx-auto px-6 py-4 flex items-center justify-between">
        <div 
          className="logo cursor-pointer"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <div className="text-3xl font-playfair font-bold text-gold tracking-wider hover:text-gold-light transition-colors duration-300">
            JENMA
          </div>
          <p className="text-[10px] tracking-[0.3em] text-gray-400 uppercase">Concept</p>
        </div>
        
        {/* Logo JENMA 3D Animé - Desktop */}
        <div className="hidden lg:flex items-center gap-4">
          <Logo3DAnimated size="normal" />
        </div>
        
        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-5 xl:space-x-8">
          <button 
            onClick={() => scrollToSection('about')}
            className="nav-link text-sm tracking-wide text-gray-300 hover:text-gold transition-colors duration-300 uppercase"
          >
            À propos
          </button>
          <button 
            onClick={() => scrollToSection('expertise')}
            className="nav-link text-sm tracking-wide text-gray-300 hover:text-gold transition-colors duration-300 uppercase"
          >
            Expertise
          </button>
          <button 
            onClick={() => scrollToSection('portfolio')}
            className="nav-link text-sm tracking-wide text-gray-300 hover:text-gold transition-colors duration-300 uppercase"
          >
            Projets
          </button>
          <a
            href="/configurateur"
            data-testid="nav-configurator-link"
            className="nav-link text-sm tracking-wide text-[#4dffb8]/90 hover:text-gold transition-colors duration-300 uppercase"
          >
            Configurateur
          </a>
          <a
            href="/services"
            className="nav-link text-sm tracking-wide text-gray-300 hover:text-gold transition-colors duration-300 uppercase"
          >
            Services
          </a>
          <AccountLink user={user} />
          <Button 
            onClick={() => scrollToSection('contact')}
            className="bg-gold hover:bg-gold-light text-dark font-medium transition-all duration-300 shadow-gold"
          >
            Contact
          </Button>
        </nav>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="lg:hidden text-gold hover:text-gold-light transition-colors z-50"
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <Menu className="w-6 h-6" />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden fixed inset-0 bg-dark/98 backdrop-blur-lg transition-all duration-300 ${
          mobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        style={{ top: '80px' }}
      >
        <nav className="container mx-auto px-6 py-8 flex flex-col space-y-6">
          <button 
            onClick={() => scrollToSection('about')}
            className="text-left text-xl font-playfair text-gray-300 hover:text-gold transition-colors duration-300 uppercase border-b border-gold/10 pb-4"
          >
            À propos
          </button>
          <button 
            onClick={() => scrollToSection('expertise')}
            className="text-left text-xl font-playfair text-gray-300 hover:text-gold transition-colors duration-300 uppercase border-b border-gold/10 pb-4"
          >
            Expertise
          </button>
          <button 
            onClick={() => scrollToSection('portfolio')}
            className="text-left text-xl font-playfair text-gray-300 hover:text-gold transition-colors duration-300 uppercase border-b border-gold/10 pb-4"
          >
            Projets
          </button>
          <a
            href={user ? '/mon-espace' : '/connexion'}
            data-testid="mobile-nav-account-link"
            onClick={handleServicesClick}
            className="text-left text-xl font-playfair text-gray-300 hover:text-gold transition-colors duration-300 uppercase border-b border-gold/10 pb-4"
          >
            {user ? 'Mon espace' : 'Connexion'}
          </a>
          <a
            href="/configurateur"
            data-testid="mobile-nav-configurator-link"
            onClick={handleServicesClick}
            className="text-left text-xl font-playfair text-gray-300 hover:text-gold transition-colors duration-300 uppercase border-b border-gold/10 pb-4"
          >
            Configurateur
          </a>
          <a
            href="/services"
            onClick={handleServicesClick}
            className="text-left text-xl font-playfair text-gray-300 hover:text-gold transition-colors duration-300 uppercase border-b border-gold/10 pb-4"
          >
            Services
          </a>
          <Button 
            onClick={() => scrollToSection('contact')}
            className="bg-gold hover:bg-gold-light text-dark font-medium transition-all duration-300 shadow-gold text-lg py-6 mt-4"
          >
            Contact
          </Button>
          
          {/* Logo 3D in mobile menu */}
          <div className="pt-8 flex justify-center">
            <Logo3DAnimated size="small" />
          </div>
        </nav>
      </div>
    </header>
  );
};
